// The meeting voice pass and voice signatures in the meeting UI (BUILD 2 C2-6,
// WP2-4). Slots in BUILD 1's meeting files:
//   meeting-speakers.tsx          SpeakerVoiceSuggestions on each voice chip,
//                                 VoiceStatusLine and VoiceReviewEntry in the panel
//   meeting-transcript.tsx        RowVoiceFlag beside a row's speaker
//   meeting-record-control.tsx    RecordVoiceNotice in the Record panel, and
//                                 with the opt-out sent atomically when recording starts
// Suggestions carry no scores. Strong recognition has separate provenance and Undo.
// The popover's `hint` highlights a voice and never preselects it.
//
// WP2-0b slots: render nothing, and do nothing, until WP2-4 fills them.
import { useEffect, useRef, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { useAppCatalogLocale } from '@/i18n/context';
import { identificationWriter } from './meeting-voice-identification';
import { voiceApi } from '@/company/voice-api';
import { useCompany } from '@/company/context';
import meetingsVoiceCopy from '@/i18n/ns/meetings-voice';
import { meetingsApi } from './meetings-api';
import { useWorkspace } from './context';
import {
  participantFor,
  SpeakerTargetPicker,
  useCompanyMembers,
  useSpeakersCopy,
  type SpeakerTarget,
} from './meeting-speaker-popover';
import './meeting-voice.css';
import type { MeetingChanged, MeetingPlayerRef } from './meeting-slots';
import type { CompanyMeeting, MeetingSegment, MeetingSpeaker, MeetingVoiceReview } from './types';

/** "Sounds like …", merge and identity-merge suggestions for one voice. */
export function SpeakerVoiceSuggestions({
  meeting,
  voice,
  onChanged,
}: {
  meeting: CompanyMeeting;
  voice: MeetingSpeaker;
  onChanged: MeetingChanged;
}) {
  const copy = useNamespaceCopy(meetingsVoiceCopy);
  const ws = useWorkspace();
  const [busy, setBusy] = useState<string | null>(null);
  const suggestions = (voice.voiceSuggestions ?? []).filter(
    (suggestion) => suggestion.kind === 'merge' || meeting.voice?.identification === true,
  );
  const canReview = meeting.canEdit && meeting.voice?.status === 'current';
  if (!voice.voiceRecognition && !canReview) return null;
  const run = async (id: string, action: () => Promise<unknown>) => {
    setBusy(id);
    try {
      await action();
      onChanged();
    } catch (error) {
      ws.notify(copy.errorSuggestionGone);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="ws-voice-suggestions" aria-label={copy.voiceMatch}>
      {voice.voiceRecognition && (
        <div className="ws-voice-suggestion">
          <span>{copy.recognisedByVoice}</span>
          {meeting.canEdit && (
            <button
              className="ws-button"
              disabled={busy !== null}
              onClick={() =>
                void run(voice.voiceRecognition!.id, () =>
                  meetingsApi.undoVoiceRecognition(
                    meeting.id,
                    voice.label,
                    voice.voiceRecognition!.id,
                  ),
                )
              }
            >
              {copy.undoRecognition}
            </button>
          )}
        </div>
      )}
      {canReview &&
        suggestions.map((suggestion) => {
          const person = suggestion.person?.displayName;
          const label =
            suggestion.kind === 'merge'
              ? formatCopy(copy.sameVoiceAs, { speaker: suggestion.otherLabel ?? '' })
              : suggestion.kind === 'identity_merge'
                ? formatCopy(copy.bothSoundLike, {
                    a: voice.label,
                    b: suggestion.otherLabel ?? '',
                    name: person ?? '',
                  })
                : formatCopy(copy.soundsLike, { name: person ?? '' });
          return (
            <div className="ws-voice-suggestion" key={suggestion.id}>
              <div className="ws-voice-suggestion-copy">
                {suggestion.person?.avatarUrl && (
                  <img src={suggestion.person.avatarUrl} alt="" width={28} height={28} />
                )}
                <span>{label}</span>
                <small>{copy.voiceMatch}</small>
              </div>
              <div className="ws-voice-suggestion-actions">
                <button
                  className="ws-button"
                  disabled={busy !== null}
                  onClick={() =>
                    void run(suggestion.id, () =>
                      meetingsApi.acceptVoiceSuggestion(meeting.id, voice.label, suggestion.id),
                    )
                  }
                >
                  {suggestion.kind === 'identity_merge'
                    ? copy.mergeAndName
                    : suggestion.kind === 'merge'
                      ? copy.merge
                      : copy.confirm}
                </button>
                <button
                  className="ws-citation"
                  disabled={busy !== null}
                  onClick={() =>
                    void run(suggestion.id, () =>
                      meetingsApi.dismissVoiceSuggestion(meeting.id, voice.label, suggestion.id),
                    )
                  }
                >
                  {person ? formatCopy(copy.notThisPerson, { name: person }) : copy.notSame}
                </button>
              </div>
            </div>
          );
        })}
    </div>
  );
}

/** Checking voices, a failed check with Retry, "Check voices again", identification off. */
export function VoiceStatusLine({
  meeting,
  onChanged,
}: {
  meeting: CompanyMeeting;
  onChanged: MeetingChanged;
}) {
  const copy = useNamespaceCopy(meetingsVoiceCopy);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const status = meeting.voice?.status;
  if (!meeting.canEdit || !meeting.voice || status === 'none') return null;
  if (status === 'current') return <RecordVoiceNotice meeting={meeting} onChanged={onChanged} />;
  if (status === 'queued' || status === 'running')
    return (
      <p className="ws-speaker-status" role="status">
        {copy.checkingVoices}
      </p>
    );
  if (status === 'skipped' && meeting.voice.skipReason === 'disabled')
    return <p className="ws-page-note">{copy.identificationOff}</p>;
  const message =
    status === 'skipped' && meeting.voice.skipReason === 'predates_voice_pass'
      ? copy.predatesVoicePass
      : copy.voiceCheckFailed;
  return (
    <div className="ws-voice-status" role="status">
      <span>{message}</span>
      <button
        className="ws-button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(false);
          try {
            await meetingsApi.rerunVoice(meeting.id);
            onChanged();
          } catch {
            setError(true);
          } finally {
            setBusy(false);
          }
        }}
      >
        {copy.checkVoicesAgain}
      </button>
      {error && <p role="alert">{copy.errorUnavailable}</p>}
    </div>
  );
}

/** The entry to the uncertain-rows review queue. */
export function VoiceReviewEntry({
  meeting,
  player,
  onChanged,
}: {
  meeting: CompanyMeeting;
  player: MeetingPlayerRef;
  onChanged: MeetingChanged;
}) {
  const copy = useNamespaceCopy(meetingsVoiceCopy);
  const locale = useAppCatalogLocale();
  const number = new Intl.NumberFormat(locale);
  const ws = useWorkspace();
  const [review, setReview] = useState<{ rows: MeetingVoiceReview['rows']; total: number } | null>(
    null,
  );
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState<SpeakerTarget | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const members = useCompanyMembers(Boolean(meeting.canEdit));
  const speakersCopy = useSpeakersCopy();
  const epoch = useRef(0);
  const pending = useRef(false);
  const stopSample = useRef<(() => void) | null>(null);
  useEffect(() => () => stopSample.current?.(), [meeting.id, index]);
  useEffect(() => {
    epoch.current++;
    pending.current = false;
    setReview(null);
    setIndex(0);
    setTarget(null);
    setPickerOpen(false);
    setBusy(false);
    return () => {
      epoch.current++;
    };
  }, [meeting.id, meeting.voice?.generatedRevision, meeting.voice?.status]);
  const rows = review?.rows ?? [];
  const row = rows[index];
  if (!meeting.canEdit || meeting.voice?.status !== 'current' || meeting.voice.uncertainRows < 1)
    return null;
  const load = async () => {
    if (pending.current) return;
    pending.current = true;
    const version = epoch.current;
    setBusy(true);
    try {
      const loaded = await meetingsApi.voiceReview(meeting.id);
      if (epoch.current !== version) return;
      setReview(loaded);
      setIndex(0);
    } catch (error) {
      if (epoch.current === version) ws.notify(copy.errorUnavailable);
    } finally {
      if (epoch.current === version) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  const act = async (action: () => Promise<unknown>) => {
    if (pending.current) return;
    pending.current = true;
    const version = epoch.current;
    setBusy(true);
    try {
      await action();
      if (epoch.current !== version) return;
      setTarget(null);
      setPickerOpen(false);
      if (index + 1 < rows.length) setIndex(index + 1);
      else {
        setReview(null);
        onChanged();
      }
    } catch (error) {
      if (epoch.current === version) ws.notify(copy.errorUnavailable);
    } finally {
      if (epoch.current === version) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  const moveRow = async () => {
    if (!target || target.kind === 'same') return;
    if (target.kind === 'voice')
      return meetingsApi.setSegmentSpeaker(meeting.id, row.index, {
        scope: 'row',
        label: target.label,
      });
    if (target.kind === 'not_participant') return;
    if (target.kind === 'participant' || target.kind === 'member' || target.kind === 'guest') {
      const participantId = await participantFor(meeting.id, target);
      if (participantId)
        return meetingsApi.setSegmentSpeaker(meeting.id, row.index, {
          scope: 'row',
          participantId,
        });
    }
  };
  if (!review)
    return (
      <button className="ws-button" disabled={busy} onClick={() => void load()}>
        {formatCopy(copy.reviewUncertain, { count: number.format(meeting.voice.uncertainRows) })}
      </button>
    );
  if (!row) return null;
  if (pickerOpen)
    return (
      <div className="ws-voice-review" role="region" aria-label={copy.reviewUncertain}>
        <p>{formatCopy(copy.uncertainHint, { speaker: row.bestOtherLabel })}</p>
        <SpeakerTargetPicker
          meeting={meeting}
          members={members}
          copy={speakersCopy}
          options={{
            voices: { except: row.label, hint: row.bestOtherLabel },
          }}
          onPick={setTarget}
        />
        <button
          className="ws-button primary"
          disabled={busy || !target || target.kind === 'same'}
          onClick={() => void act(moveRow)}
        >
          {speakersCopy.onlyThisRow}
        </button>
        <button
          className="ws-button"
          disabled={busy}
          onClick={() => {
            setTarget(null);
            setPickerOpen(false);
          }}
        >
          {speakersCopy.cancel}
        </button>
      </div>
    );
  const play = () => {
    if (row.startMs === null || row.endMs === null || !player.current) return;
    stopSample.current?.();
    const media = player.current;
    const stop = () => {
      media.pause();
      media.removeEventListener('timeupdate', onTime);
      stopSample.current = null;
    };
    const onTime = () => {
      if (media.currentTime * 1000 >= row.endMs!) stop();
    };
    stopSample.current = stop;
    media.addEventListener('timeupdate', onTime);
    media.currentTime = row.startMs / 1000;
    void media.play().catch(stop);
  };
  return (
    <div className="ws-voice-review" role="region" aria-label={copy.reviewUncertain}>
      <p>
        {formatCopy(copy.progress, {
          n: number.format(index + 1),
          total: number.format(review.total),
        })}
      </p>
      <p>{formatCopy(copy.uncertainHint, { speaker: row.bestOtherLabel })}</p>
      <button className="ws-citation" aria-label={speakersCopy.playSamples} onClick={play}>
        ▶
      </button>
      <div className="ws-voice-review-actions">
        <button
          className="ws-button"
          disabled={busy}
          onClick={() => void act(() => meetingsApi.markVoiceReviewed(meeting.id, row.index))}
        >
          {formatCopy(copy.keepAs, { name: row.label })}
        </button>
        <button
          className="ws-button"
          disabled={busy}
          onClick={() =>
            void act(() =>
              meetingsApi.setSegmentSpeaker(meeting.id, row.index, {
                scope: 'row',
                label: row.bestOtherLabel,
              }),
            )
          }
        >
          {formatCopy(copy.moveTo, { name: row.bestOtherLabel })}
        </button>
        <button
          className="ws-citation"
          disabled={busy}
          onClick={() => {
            setTarget(null);
            setPickerOpen(true);
          }}
        >
          {copy.someoneElse}
        </button>
        <button
          className="ws-citation"
          disabled={busy}
          onClick={() => void act(() => meetingsApi.markVoiceReviewed(meeting.id, row.index))}
        >
          {copy.skip}
        </button>
      </div>
    </div>
  );
}

/** The "?" on an uncertain row; opening it opens the row's speaker popover. */
export function RowVoiceFlag({
  segment,
  onOpen,
}: {
  meeting: CompanyMeeting;
  segment: MeetingSegment;
  onOpen: () => void;
}) {
  const copy = useNamespaceCopy(meetingsVoiceCopy);
  if (!segment.voice?.uncertain) return null;
  return (
    <button
      className="ws-voice-flag"
      aria-label={copy.uncertainSpeaker}
      title={copy.uncertainSpeaker}
      onClick={onOpen}
    >
      ?
    </button>
  );
}

/** Recording and ready-meeting identification choices use the same serialized writer. */
export function RecordVoiceNotice({
  meeting,
  onChanged,
  onPendingChange,
}: {
  meeting?: CompanyMeeting;
  onChanged?: MeetingChanged;
  onPendingChange?: (enabled: boolean) => void;
} = {}) {
  const copy = useNamespaceCopy(meetingsVoiceCopy);
  const companyId = useCompany().data?.company?.id;
  const [orgOn, setOrgOn] = useState(false);
  useEffect(() => {
    let current = true;
    setOrgOn(false);
    void voiceApi
      .me()
      .then((me) => {
        if (current) setOrgOn(me.available && me.org.signaturesEnabled);
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [companyId]);
  const [saveError, setSaveError] = useState(false);
  const [enabled, setEnabled] = useState(meeting?.voice?.identification ?? true);
  useEffect(() => {
    setEnabled(meeting?.voice?.identification ?? true);
    setSaveError(false);
  }, [meeting?.id, meeting?.voice?.identification]);
  useEffect(() => {
    const update = (event: Event) => {
      const result = (event as CustomEvent<{ meetingId: string; failed: boolean }>).detail;
      if (meeting && result.meetingId !== meeting.id) return;
      setSaveError(result.failed);
      if (!result.failed) onChanged?.();
    };
    window.addEventListener('poneva:voice-identification', update);
    return () => window.removeEventListener('poneva:voice-identification', update);
  }, [meeting?.id, onChanged]);
  if (meeting && !meeting.canEdit) return null;
  if (!orgOn) return null;
  return (
    <div>
      {orgOn && <p className="ws-page-note">{copy.consentLineVoice}</p>}
      <label className="ws-voice-record-notice">
        <input
          type="checkbox"
          checked={!enabled}
          onChange={(event) => {
            const next = !event.target.checked;
            setEnabled(next);
            setSaveError(false);
            if (meeting) identificationWriter.set(meeting.id, next);
            else onPendingChange?.(next);
          }}
        />
        {copy.turnOffVoiceSuggestions}
        {saveError && <span role="alert">{copy.errorUnavailable}</span>}
      </label>
    </div>
  );
}
